import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable copy button component with visual feedback
 * 
 * @param {Object} props
 * @param {Function} props.onCopy - Function that returns the text to copy
 * @param {string} props.ariaLabel - ARIA label for accessibility
 * @param {string} props.title - Tooltip text
 * @param {string} props.colorTheme - Color theme for focus ring ('blue', 'green', 'gray', etc.)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.position - Position classes (default: 'absolute top-2 right-10')
 */
const CopyButton = ({ 
  onCopy, 
  ariaLabel, 
  title, 
  colorTheme = 'blue',
  className = '',
  position = 'absolute top-2 right-10',
}) => {
  const [copied, setCopied] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    
    try {
      const textToCopy = typeof onCopy === 'function' ? onCopy() : onCopy;
      
      if (!textToCopy) {
        console.warn('CopyButton: No content to copy');
        return;
      }

      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      
      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000);
      
      // Fallback for older browsers
    } catch (err) {
      console.error('Failed to copy:', err);
      
      // Fallback: use execCommand
      try {
        const textArea = document.createElement('textarea');
        textArea.value = typeof onCopy === 'function' ? onCopy() : onCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        alert('Failed to copy to clipboard. Please select and copy manually.');
      }
    }
  };

  const themeFocusRing = {
    blue: 'focus:ring-blue-500',
    green: 'focus:ring-green-500',
    gray: 'focus:ring-gray-500',
    amber: 'focus:ring-amber-500',
    red: 'focus:ring-red-500',
  }[colorTheme] || 'focus:ring-blue-500';

  return (
    <>
      <button
        onClick={handleClick}
        className={`${position} p-1.5 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded transition-colors z-10 focus:outline-none focus:ring-2 ${themeFocusRing} focus:ring-offset-2 focus:ring-offset-gray-900 ${className}`}
        aria-label={ariaLabel || title || 'Copy to clipboard'}
        title={title || ariaLabel || 'Copy to clipboard'}
        aria-live="polite"
      >
        {copied ? (
          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
      {copied && (
        <span className="sr-only" aria-live="polite">
          Copied to clipboard
        </span>
      )}
    </>
  );
};

CopyButton.propTypes = {
  onCopy: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  ariaLabel: PropTypes.string,
  title: PropTypes.string,
  colorTheme: PropTypes.oneOf(['blue', 'green', 'gray', 'amber', 'red']),
  className: PropTypes.string,
  position: PropTypes.string,
};

export default CopyButton;

